/**
 * 避難先ナレッジストア (Pinia)。
 *
 * 判断・修正のレコード (KnowledgeRecord) が蓄積される「生きた」ストア。
 * 永続化方式は他3ストア (v1/v2/喫煙所) と共通の分岐:
 * - Firebase 未設定: localStorage (persisted-crud) + 同梱シード136件
 * - 設定済み: Firestore (firestore-crud)。初回は ensureSeedsKnowledge がシード注入。
 *
 * 蓄積の入口は recordDecision() 1本:
 * - 同一物件×同一スロットで同じ避難先の再保存 → 既存レコードを更新 (upsert)
 * - 別の避難先へ変わった → previous を自動で埋めた新レコード (= 修正の履歴が自然に残る)
 * ヒント (deriveHints/rankWithKnowledge) は records を照会するため、
 * 記録した瞬間から次の判断に効く。
 */
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import type {
  KnowledgeBasis,
  KnowledgeRecord,
} from '@/types/knowledge';
import type { PropertyKind } from '@/types/property';
import { generateId, nowIso } from '@/lib/id';
import { createPersistedCrud, type PersistedCrud } from '@/lib/stores/persisted-crud';
import { createFirestoreCrud, type FirestoreCrud } from '@/lib/stores/firestore-crud';
import { isFirebaseConfigured } from '@/lib/firebase/init';
import { ensureSeedsKnowledge } from '@/lib/firebase/bootstrap';
import { useAuthStore } from '@/stores/authStore';
import { currentEditor, withEditStamp } from './edit-stamp';
import type { SaveState } from '@/lib/firebase/save-pipeline';
import { cloneKnowledgeSeeds } from '@/lib/knowledge/datasets';
import { parsePropertyAddress } from '@/lib/knowledge/address-match';
import { sameFacilityName } from '@/lib/knowledge/normalize';

const STORAGE_KEY = 'evacuation-map.knowledge.v1';

let storeInstantiated = false;

function watchBinding<S, D extends S>(src: Ref<D>, dst: Ref<S>): () => void {
  return watch(
    src,
    (v) => {
      dst.value = v;
    },
    { immediate: true },
  );
}

/** ストア未生成なら何もしない、生成済みなら flush する (ログアウト時用)。 */
export async function flushKnowledgeStoreIfActive(): Promise<void> {
  if (!storeInstantiated) return;
  const store = useKnowledgeStore();
  await store.flushAll();
}

/** recordDecision の入力。source→basis の変換は呼び出し側 (editor) が行う。 */
export interface RecordDecisionInput {
  propertyId: string;
  slot: 'evacuation' | 'evacuationArea' | 'shelter';
  propertyName: string;
  address: string;
  propertyType?: PropertyKind;
  decision: { name: string; category?: string };
  basis: KnowledgeBasis;
  reason?: string;
  evidenceUrls?: string[];
}

export const useKnowledgeStore = defineStore('knowledge', () => {
  storeInstantiated = true;

  const firestoreMode = isFirebaseConfigured();
  const crud = shallowRef<
    PersistedCrud<KnowledgeRecord> | FirestoreCrud<KnowledgeRecord> | undefined
  >(undefined);
  const records = computed(() => crud.value?.items.value ?? []);
  const ready = ref(false);
  const saveState = ref<SaveState | 'local'>('local');

  function getRecord(id: string): KnowledgeRecord | undefined {
    return crud.value?.get(id);
  }

  function addRecord(record: KnowledgeRecord): void {
    crud.value?.insert(record);
  }

  function updateRecord(
    id: string,
    patch: Partial<Omit<KnowledgeRecord, 'id'>>,
  ): void {
    crud.value?.update(id, withEditStamp(patch));
  }

  function deleteRecord(id: string): void {
    crud.value?.remove(id);
  }

  /**
   * 避難先判断を記録する (R5: 理由を構造化して残す)。
   * 住所から区が取れない場合は照合に使えないため記録しない (null を返す)。
   */
  function recordDecision(input: RecordDecisionInput): KnowledgeRecord | null {
    const parsed = parsePropertyAddress(input.address);
    const ward = parsed.ward;
    if (!ward) return null;

    const chain = records.value
      .filter(
        (r) =>
          r.sourceTag === 'app' &&
          r.propertyId === input.propertyId &&
          r.slot === input.slot,
      )
      .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
    const latest = chain[chain.length - 1];

    if (
      latest?.decision &&
      sameFacilityName(latest.decision.name, input.decision.name)
    ) {
      // 同じ避難先の再保存 → 出典情報だけ更新 (レコードは増やさない)
      updateRecord(latest.id, {
        decision: input.decision,
        basis: input.basis,
        reason: input.reason,
        evidenceUrls: input.evidenceUrls,
        propertyType: input.propertyType,
        recordedBy: currentEditor(),
        recordedAt: nowIso(),
      });
      return getRecord(latest.id) ?? latest;
    }

    const record: KnowledgeRecord = {
      id: generateId('knowledge'),
      address: input.address,
      pref: parsed.pref,
      city: parsed.city,
      ward,
      town: parsed.town,
      chome: parsed.chome != null ? [parsed.chome] : undefined,
      propertyName: input.propertyName,
      propertyType: input.propertyType,
      propertyId: input.propertyId,
      slot: input.slot,
      decision: input.decision,
      // 避難先が変わった場合は修正として履歴が残る (蓄積経路②)
      previous: latest?.decision
        ? { name: latest.decision.name, category: latest.decision.category }
        : undefined,
      basis: input.basis,
      reason: input.reason,
      evidenceUrls: input.evidenceUrls,
      // localStorage モード (未ログイン) では editor が取れない
      recordedBy: currentEditor() ?? 'local',
      recordedAt: nowIso(),
      sourceTag: 'app',
      updatedAt: nowIso(),
    };
    crud.value?.insert(record);
    return record;
  }

  function flushAll(): Promise<void> {
    const c = crud.value;
    return c && 'flushAll' in c ? c.flushAll() : Promise.resolve();
  }

  if (firestoreMode) {
    // ---- Firestore モード — smokingStore と同型 ----
    const auth = useAuthStore();
    let boundEmail: string | null = null;
    let stopBindings: Array<() => void> = [];
    let disposeCurrent: (() => void) | null = null;

    const initFirestore = (email: string, displayName: string | null): void => {
      for (const stop of stopBindings) stop();
      stopBindings = [];
      const fsCrud = createFirestoreCrud<KnowledgeRecord>({
        kind: 'knowledge_records',
        email,
        displayName,
        deferLoad: true,
      });
      crud.value = fsCrud;
      stopBindings.push(watchBinding(fsCrud.ready, ready));
      stopBindings.push(watchBinding(fsCrud.saveState, saveState));
      disposeCurrent = fsCrud.dispose;
      boundEmail = email;
      void (async () => {
        try {
          await ensureSeedsKnowledge(email, displayName);
        } catch (e) {
          console.warn('[knowledgeStore] シード注入に失敗 (閲覧は継続)', e);
        }
        fsCrud.startLoad();
      })();
    };

    watch(
      () => auth.status,
      (status) => {
        if (status === 'ready' && auth.email && auth.email !== boundEmail) {
          disposeCurrent?.();
          initFirestore(auth.email, auth.displayName);
        } else if (status !== 'ready') {
          boundEmail = null;
          ready.value = false;
        }
      },
      { immediate: true },
    );
  } else {
    // ---- localStorage モード ----
    const firstRun = localStorage.getItem(STORAGE_KEY) === null;
    crud.value = createPersistedCrud<KnowledgeRecord>({
      storageKey: STORAGE_KEY,
      label: '避難先ナレッジ',
      initial: cloneKnowledgeSeeds,
    });
    if (firstRun) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(records.value));
      } catch (e) {
        console.warn('避難先ナレッジの初期保存に失敗しました', e);
      }
    }
    ready.value = true;
  }

  return {
    records,
    ready,
    saveState,
    flushAll,
    getRecord,
    addRecord,
    updateRecord,
    deleteRecord,
    recordDecision,
  };
});
